import React, { useState, useCallback } from 'react';
import { X, Upload, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import VideoPreview from './VideoPreview';
import ImageCropTool from './common/ImageCropTool';

interface StoryUploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (file: File, data: {
    description?: string;
    location?: string;
    privacy?: 'public' | 'friends' | 'private';
  }) => void;
}

const StoryUploadPopup: React.FC<StoryUploadPopupProps> = ({ isOpen, onClose, onSubmit }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState<'public' | 'friends' | 'private'>('public');
  const [isUploading, setIsUploading] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState<string>('');

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('📁 StoryUploadPopup handleFileSelect - raw file:', {
      file,
      hasFiles: !!event.target.files,
      filesLength: event.target.files?.length,
      firstFile: event.target.files?.[0]
    });
    
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      console.log('✅ StoryUploadPopup - valid file selected:', {
        name: file.name,
        size: file.size,
        type: file.type,
        isFile: file instanceof File
      });
      setSelectedFile(file);
    } else {
      console.log('❌ StoryUploadPopup - invalid file or no file selected');
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || !onSubmit) return;

    console.log('📚 StoryUploadPopup handleSubmit - selectedFile:', {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      fileObject: selectedFile,
      isFile: selectedFile instanceof File
    });

    setIsUploading(true);
    try {
      await onSubmit(selectedFile, {
        description,
        privacy,
      });
    } catch (error) {
      console.error('Story upload error:', error);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setDescription('');
      onClose();
    }
  }, [selectedFile, description, privacy, onSubmit, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Story</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {!selectedFile ? (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="flex space-x-4">
                <ImageIcon className="h-12 w-12 text-gray-400" />
                <VideoIcon className="h-12 w-12 text-gray-400" />
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Select an image or video for your story
            </p>
            <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
              <Upload className="h-4 w-4 mr-2" />
              Choose File
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Selected file:</p>
              <p className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                {videoThumbnail && selectedFile.type.startsWith('video/') && ' • Thumbnail generated'}
              </p>
            </div>

            {selectedFile.type.startsWith('video/') && (
              <VideoPreview 
                file={selectedFile} 
                onThumbnailGenerated={(thumbnail) => setVideoThumbnail(thumbnail)}
              />
            )}

            {selectedFile.type.startsWith('image/') && (
              <ImageCropTool 
                imageFile={selectedFile}
                cropType="portrait"
                aspectRatio={9/16}
                onCropComplete={(croppedFile, _croppedDataUrl) => {
                  // Handle cropped image
                  setSelectedFile(croppedFile);
                }}
                onCancel={() => {
                  // Handle cancel - could show original image
                }}
              />
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description to your story..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Privacy
              </label>
              <select
                value={privacy}
                onChange={(e) => setPrivacy(e.target.value as 'public' | 'friends' | 'private')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="public">Public</option>
                <option value="friends">Friends Only</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setSelectedFile(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Change File
              </button>
              <button
                onClick={handleSubmit}
                disabled={isUploading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Uploading...' : 'Upload Story'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryUploadPopup;
